import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork } from 'app/core/core.module';
import { PurchaseOrderInvoice } from 'app/core/model/purchase-order-invoice';
import { SharedModule } from 'app/shared/shared.module';
import { confirm } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PurchaseOrderInvoiceDialogComponent } from '../purchase-order-invoice-dialog/purchase-order-invoice-dialog.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import moment from 'moment';
import { alert } from 'devextreme/ui/dialog';
import { PurchaseOrder } from 'app/core/model/purchase-order';

@Component({
    selector: 'app-purchase-order-billing',
    templateUrl: './purchase-order-billing.component.html',
    styleUrls: ['./purchase-order-billing.component.scss'],
    standalone: false,
})
export class PurchaseOrderBillingComponent {
    invoices: PurchaseOrderInvoice[];

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(private uow: GenesisUnitOfWork, private router: Router, public auth: AuthService, private modalService: BsModalService, private appInfo: AppInformationService) {}

    async ngOnInit() {
        await this.getData();
    }

    async getData() {
        this.invoices = (await this.uow.purchaseOrderInvoices.whereActive(null, 'purchaseOrderInvoiceLines, vendor')) as PurchaseOrderInvoice[];
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async rowDoubleClick(e) {
        if (!e.data) return;
        await this.open(e.data);
    }

    async open(invoice: PurchaseOrderInvoice) {
        if (!invoice) return;

        const modalRef = this.modalService.show(PurchaseOrderInvoiceDialogComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                invoice: invoice,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async delete(invoice: PurchaseOrderInvoice) {
        if (!invoice) return;

        const success = await confirm('Are you sure you want to DELETE this record?', `${this.appInfo.settings.company}`);
        if (!success) return;

        if (invoice.quickBooksId !== null) {
            this.appInfo.pleaseWaitHide();
            const result = await confirm(
                `This record has already been exported to QuickBooks.  If you delete it, it will need to be manually deleted from QuickBooks. Continue?`,
                this.appInfo.settings.company
            );
            if (!result) return false;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Enter Reason For Deletion',
                dialogPrompt: 'Enter Reason For Deletion',
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.result && !!result.value && result.value.length > 0) {
                this.appInfo.pleaseWaitShow();
                const note = `Deleted ${moment(result.deleted).format('MM/DD/YYYY')} by ${this.auth.currentUser.userName}: ${result.value}`;
                invoice.note = invoice.note ? invoice.note + '\n' + note : note;
                invoice.deleted = new Date();
                invoice.deletedBy = this.auth.currentUser.userName;
                invoice.purchaseOrderInvoiceLines.forEach((line) => {
                    line.deleted = new Date();
                    line.deletedBy = this.auth.currentUser.userName;
                });
                const PO = ((await this.uow.getPurchaseOrderByPurchaseOrderInvoiceId(invoice.id)) as PurchaseOrder[])[0];
                if (PO) {
                    const PONote = `PO Invoice #${invoice.referenceNumber} Deleted ${moment(result.deleted).format('MM/DD/YYYY')} by ${this.auth.currentUser.userName}: ${result.value}`;
                    PO.note = PO.note ? PO.note + '\n' + PONote : PONote;
                }
                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            } else {
                await alert('Reason required.', this.appInfo.settings.company);
            }
        });
    }

    async readyToExport(invoices: PurchaseOrderInvoice[]) {
        if (!invoices) return;

        const result = await confirm(
            `Are you sure you want to mark the selected Invoice${invoices.length > 1 ? 's' : ''} as Ready To Export for Quickbooks?`,
            this.appInfo.settings.company
        );
        if (!result) return;

        invoices.forEach((invoice) => {
            if (!invoice.readyToExport) invoice.readyToExport = new Date();
        });
        await this.uow.commit();
        await this.getData();
    }

    async payWithoutQuickBooks(invoice: PurchaseOrderInvoice) {
        if (!invoice) return;

        const success = await confirm('Are you sure you want to mark this Purchase Order Invoice as paid without QuickBooks?', this.appInfo.settings.company);
        if (!success) return;

        invoice.quickBooksLastExport = new Date();
        invoice.quickBooksId = 'Marked paid w/o QB';
        await this.uow.commit([invoice]);
        this.dataGrid.instance.clearSelection();
    }
}
